import React from 'react';
import {InstalledBaseNodeType} from "../../../codegen/graphql";
import {getInstalledBaseTypeIcon} from "@dreebit-registry/vsm-microservice-components";
import {Box, IconButton, Typography} from "@mui/joy";
import {EditRounded} from "@mui-symbols-material/w300";
import {useTranslation} from "react-i18next";
import {getInstalledBaseNodeTypeName} from "../../../utils/getInstalledBaseNodeTypeName";

interface Props {
    onNodeTypeSelect?: (nodeType: InstalledBaseNodeType) => void;
    nodeType: InstalledBaseNodeType,
    /** When provided, the card acts as a read-only display of the selected type with a
     *  trailing edit affordance; clicking the card or the icon calls onEdit. */
    onEdit?: () => void;
    /** Structurally not selectable (e.g. count limit reached); shown greyed out. */
    disabled?: boolean;
    /** Short explanation rendered instead of the description while disabled. */
    disabledHint?: string;
}

const NodeTypeSelectionItem = ({onNodeTypeSelect, nodeType, onEdit, disabled, disabledHint}: Props) => {
    const {t} = useTranslation();
    // Without any handler the card is a purely static display (no pointer/hover).
    const interactive = !disabled && (!!onEdit || !!onNodeTypeSelect);
    return <Box
        onClick={interactive ? () => (onEdit ? onEdit() : onNodeTypeSelect?.(nodeType)) : undefined}
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            padding: '14px 16px',
            border: '1px solid',
            borderColor: '#ccc',
            borderRadius: '5px',
            backgroundColor: 'transparent',
            ...(interactive && {
                cursor: 'pointer',
                transition: 'border-color 0.15s, background-color 0.15s',
                '&:hover': {
                    borderColor: '#999',
                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                },
            }),
            ...(disabled && {
                opacity: 0.55,
                cursor: 'not-allowed',
            }),
        }}
    >
        <Box sx={{display: 'flex', flexShrink: 0, color: '#666'}}>
            {getInstalledBaseTypeIcon(nodeType.key, 40, '#666')}
        </Box>
        <Box sx={{display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0}}>
            <Typography level={'body-sm'} sx={{fontWeight: 'bold', color: '#000'}}>{getInstalledBaseNodeTypeName(nodeType, t)}</Typography>
            {disabled && disabledHint ? (
                <Typography level={'body-sm'} sx={{color: '#666'}}>
                    {disabledHint}
                </Typography>
            ) : nodeType.description && (
                <Typography level={'body-sm'} sx={{color: '#666'}}>
                    {nodeType.description}
                </Typography>
            )}
        </Box>
        {onEdit && (
            <>
                <Box sx={{flexGrow: 1}}/>
                <IconButton
                    size={'sm'}
                    variant={'plain'}
                    onClick={(e) => {
                        e.stopPropagation();
                        onEdit();
                    }}
                >
                    <EditRounded fontSize={'small'}/>
                </IconButton>
            </>
        )}
    </Box>
};

export default NodeTypeSelectionItem;
